import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang, UI } from "@/i18n/lang";
import { PROJECTS, CATEGORIES } from "@/content/site";
import { Reveal } from "@/lib/motion";
import ProjectCard from "@/components/ProjectCard";
import { Seo, ORIGIN } from "@/components/Seo";
import { useLocaleRoute, localizePath, coreAlternates } from "@/i18n/routes";

export default function Work({ locale = "de" }) {
  const { t } = useLang();
  useLocaleRoute(locale, localizePath("/work", locale === "de" ? "en" : "de"));
  const [active, setActive] = useState("all");
  const filtered = active === "all" ? PROJECTS : PROJECTS.filter((p) => p.category === active);

  return (
    <div data-testid="work-page">
      <Seo
        title={UI.seo.work.title[locale]}
        description={UI.seo.work.desc[locale]}
        path={localizePath("/work", locale)}
        lang={locale}
        image={`${ORIGIN}/og-work.jpg`}
        alternates={coreAlternates("/work")}
      />
      <section className="pt-36 md:pt-44 pb-16 border-b border-border">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
          <Reveal>
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-bronze">— {t(UI.work.title)}</span>
            <h1 className="mt-6 font-serif text-5xl md:text-7xl lg:text-8xl tracking-tight text-ink leading-[0.98] max-w-4xl">
              {t(UI.work.intro)}
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="flex flex-wrap gap-3 mb-14" data-testid="work-filters">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                data-testid={`filter-${c.id}`}
                onClick={() => setActive(c.id)}
                className={`rounded-full px-5 py-2 text-[13px] font-medium transition-all duration-300 ${
                  active === c.id
                    ? "bg-bronze text-paper"
                    : "border border-border text-stone hover:border-ink hover:text-ink"
                }`}
              >
                {t(c.label)}
              </button>
            ))}
          </div>

          <motion.div layout className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-16">
            <AnimatePresence mode="popLayout">
              {filtered.map((p, i) => (
                <motion.div
                  key={p.slug}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.5, ease: [0.33, 1, 0.68, 1], delay: (i % 2) * 0.06 }}
                >
                  <ProjectCard project={p} aspect="aspect-[4/3]" index={i} />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
